/**
 * ProvenanceLine — the methodology pointer at the foot of the money zone.
 *
 *   "Specs as {brand} records them — how our numbers are made"
 *
 * HISTORY, because two owner rulings crossed here. FRIDGE-302 (08/27/2026)
 * ruled the full dated provenance statement — "Prices observed at N
 * retailers · verified MM/DD/YYYY · M tracked price days · …" — as the
 * citable-transparency centerpiece. FRIDGE-664 (08/31/2026) then removed the
 * counted/dated segment: the owner circled exactly that span on a live PDP
 * screenshot and wrote "remove", settling the collision with FRIDGE-626's
 * sitewide verified-date removal in FRIDGE-626's favor. What survives is the
 * tail that sat OUTSIDE his circle: the specs-source clause and the
 * methodology link. Do not re-add a count or a date here without a new
 * explicit ruling.
 *
 * Rules that still bind:
 * - Renders nothing with zero fresh offers (Real-or-Hidden) — the pointer
 *   only appears where real observations exist.
 * - FRIDGE-78 stays in force: never a relative "N ago" ticker.
 */

import Link from "next/link"

interface ProvenanceLineProps {
  /** Count of offers with a live, non-stale price on this page. Gates the
   * line: zero fresh offers renders nothing (Real-or-Hidden — a methodology
   * pointer over no observations would decorate an empty claim). */
  freshOfferCount: number
  /** Manufacturer name for the specs clause; falls back to a generic noun. */
  brand?: string | null
}

export function ProvenanceLine({ freshOfferCount, brand }: ProvenanceLineProps) {
  if (!(freshOfferCount > 0)) return null
  const specsBy = (brand || '').trim() || 'the manufacturer'

  return (
    <p
      data-testid="provenance-line"
      data-fresh-offers={freshOfferCount}
      className="mt-3 pt-3 border-t border-[#ff9951]/20 text-[11px] leading-snug text-gray-500"
    >
      Specs as {specsBy} records them —{' '}
      <Link
        href="/about-us/methodology"
        className="underline underline-offset-2 hover:text-gray-700 transition-colors"
      >
        how our numbers are made
      </Link>
    </p>
  )
}
